import type { DateTime } from 'luxon'

import { Status, SUBMITTED_BID_STATUSES } from '@/features/bidding/constants'
import type { ExtendedBid, ExtendedBidPtu } from '@/features/bidding/types/bid'

/**
 * The versions of one bid, bucketed by the status they carry, each bucket holding only its newest
 * version.
 *
 * The bid-history API comes in two shapes and bucketing is what lets one rule serve both:
 * - **slotted** (partials): at most one version per status. OFFERED, ACCEPTED and DRAFT are slots
 *   that get updated in place, so which one to use is a matter of precedence, never of ordering.
 * - **versioned** (pre-partials): every submission is its own version and the outcome overwrites its
 *   status, so the newest version can sit in any bucket and ordering is what decides.
 *
 * Placeholder statuses that never belong to a real version (TODO, NO_BID) fall outside every bucket,
 * so "the latest version" can only ever mean a version someone actually put there.
 */
export type BidVersionBuckets = {
  /** The trader's own unsent work */
  draft?: ExtendedBid
  /** What was offered: in the slotted shape the current offer, otherwise a submission with no answer yet */
  offered?: ExtendedBid
  /** What the TSO acknowledged - for ENERGY bids this is the A01 version */
  accepted?: ExtendedBid
  /** Rejected by the TSO (A02). Only exists in the versioned shape - there is no rejected slot. */
  rejected?: ExtendedBid
  /** The remaining CAPACITY outcomes: partially accepted, manually accepted or edited, overbid, buyback */
  otherOutcome?: ExtendedBid
}

const OTHER_OUTCOME_STATUSES: ReadonlySet<Status> = new Set(
  SUBMITTED_BID_STATUSES.filter(
    (status) => status !== Status.OFFERED && status !== Status.ACCEPTED && status !== Status.REJECTED,
  ),
)

export function bucketBidVersions(bids: ExtendedBid[]): BidVersionBuckets {
  return {
    draft: newestWhere(bids, (bid) => bid.status === Status.DRAFT),
    offered: newestWhere(bids, (bid) => bid.status === Status.OFFERED),
    accepted: newestWhere(bids, (bid) => bid.status === Status.ACCEPTED),
    rejected: newestWhere(bids, (bid) => bid.status === Status.REJECTED),
    otherOutcome: newestWhere(bids, (bid) => OTHER_OUTCOME_STATUSES.has(bid.status)),
  }
}

/**
 * Whether these versions are the slotted shape, where OFFERED / ACCEPTED / DRAFT hold at most one
 * version each and are updated in place.
 *
 * This only ever runs for mFRR ENERGY bids, the scope where Portfolio Manager validates expired
 * PTUs against the accepted schedule (see generateInitialBidPtus). What is left for this function is
 * the per-day exception: a delivery day that was bid before the migration still carries the old
 * shape in an environment that is otherwise migrated.
 *
 * Only structural markers are used. Both are certainties about the old shape rather than guesses:
 * - two versions sharing a status can only be the versioned shape, since a slot holds one row
 * - REJECTED and the CAPACITY outcomes are not slots, so their presence means versioned
 *
 * Deliberately NOT used: whether an ACCEPTED version carries per-PTU accepted volumes. That looks
 * like it should identify the partials shape, but several pre-partials flows populate it too -
 * manual imports and allocated accepted schedules among them - so it distinguishes nothing.
 *
 * Also deliberately NOT used as evidence either way: placeholder statuses (TODO, NO_BID). They are
 * synthesized client-side rather than carried by any real bid-history row, so their presence proves
 * nothing about the shape - they are filtered out before both checks below.
 *
 * Anything else counts as slotted, which is also where a history holding a single submission lands.
 * That is harmless: with one submission every bucket describes it, so both rules resolve to the same
 * volumes. The residual risk is a pre-migration day holding exactly one unanswered OFFERED version
 * plus one newer ACCEPTED version and no rejections, where slot precedence shows the superseded
 * offer. It affects only which values a *future* PTU is prefilled with - the trader can still edit
 * them - and no ordering of those two rows can settle it: `createdAt` is frozen in the slotted shape
 * and `updatedAt` inverts in the versioned one when a superseded submission is answered late.
 */
export function isSlottedBidHistory(bids: ExtendedBid[]): boolean {
  const slotted = [Status.DRAFT, Status.OFFERED, Status.ACCEPTED]
  const placeholders = new Set([Status.TODO, Status.NO_BID])

  const realBids = bids.filter((bid) => !placeholders.has(bid.status))

  const hasNonSlotStatus = realBids.some((bid) => !slotted.includes(bid.status))
  if (hasNonSlotStatus) {
    return false
  }

  return !slotted.some((status) => realBids.filter((bid) => bid.status === status).length > 1)
}

/**
 * The bid version a future (still editable) PTU is prefilled from.
 *
 * The trader's own draft always wins: it is unsent work that has to survive a reload, and sending
 * vacates the draft slot.
 *
 * Beyond that the shape decides, because the two shapes need opposite treatment:
 * - **slotted**: take the offer slot, which is the current offer by definition. The accepted slot is
 *   only a fallback - it holds what was acknowledged, which goes stale the moment the trader
 *   re-offers, and it cannot be compared against the offer slot because neither carries a usable
 *   timestamp.
 * - **versioned**: take the newest version whatever its outcome. This is the agreed rule (BID-209):
 *   a rejected version is still the last thing the trader sent, so it is what they want to correct
 *   rather than retype. Ordering is meaningful here, and an unanswered OFFERED version can be an
 *   older submission that a newer answered one superseded, so the buckets must be compared.
 */
export function findFuturePtuPrefillBid(bids: ExtendedBid[]): ExtendedBid | undefined {
  const buckets = bucketBidVersions(bids)

  if (buckets.draft) {
    return buckets.draft
  }

  if (isSlottedBidHistory(bids)) {
    return buckets.offered ?? buckets.accepted
  }

  return newestOf([buckets.offered, buckets.accepted, buckets.rejected, buckets.otherOutcome])
}

/**
 * The version an expired PTU is prefilled from: the acknowledged one, which is the TSO's truth for
 * rows the trader can no longer edit. No cross-slot comparison is involved.
 */
export function findAcceptedBidVersion(bids: ExtendedBid[]): ExtendedBid | undefined {
  return bucketBidVersions(bids).accepted
}

/**
 * Single source of truth for what an expired PTU must show (BID-209).
 *
 * Expired PTUs cannot be edited by the trader, so whatever we display for them is also what gets
 * re-submitted. That value has to come from the accepted version and not from the latest offer,
 * which may have been rejected: re-sending a rejected volume for a PTU in the past makes Portfolio
 * Manager refuse the whole bid.
 *
 * On an accepted baseline, Portfolio Manager only treats chunks with `acceptedQuantity != null` as
 * standing. Re-sending an unanswered chunk (no accepted quantity) with a positive volume reads as an
 * insert on a PTU that is no longer biddable and is refused; sending it back with volume 0 passes,
 * since zero-volume inserts are filtered out. The PTU-level volume still has to equal the baseline
 * row's offeredQuantity exactly, and PM only requires sum(chunks) <= volume, so every unanswered
 * chunk on an accepted source is zeroed here rather than resent as-is.
 *
 * Used by every place that fills an expired PTU: the initial prefill on the volume page and the
 * live reset when a PTU crosses its gate closure while the trader has the page open.
 *
 * @param ptuStart - Start of the expired PTU
 * @param acceptedBid - Accepted version for the activation group (from useBidOfferDerivedData)
 * @param latestNonDraftBid - Latest sent version, used when nothing was ever accepted for this PTU
 * @returns The bid PTU to take both the volume and the price chunks from, or undefined when neither
 *          source covers this PTU
 */
export function findExpiredPtuPrefillSource(
  ptuStart: DateTime,
  acceptedBid: ExtendedBid | null | undefined,
  latestNonDraftBid: ExtendedBid | null | undefined,
): ExtendedBidPtu | undefined {
  const acceptedPtu = findPtuAt(acceptedBid, ptuStart)

  if (acceptedPtu) {
    return zeroUnansweredChunks(acceptedPtu)
  }

  return findPtuAt(latestNonDraftBid, ptuStart)
}

function findPtuAt(bid: ExtendedBid | null | undefined, ptuStart: DateTime): ExtendedBidPtu | undefined {
  return bid?.ptus.find((bidPtu) => bidPtu.ptu.start.equals(ptuStart))
}

/**
 * Zeroes every chunk PM never answered (no `acceptedVolume`), leaving answered chunks untouched.
 *
 * Only meaningful for a PTU sourced from the accepted version: PM's schedules model refuses an
 * unanswered chunk resent with a positive volume as an insert on a non-biddable PTU, while a zeroed
 * one is filtered out and passes. The PTU-level `offeredVolume` is left as-is - PM diffs that against
 * the baseline row's offeredQuantity and only requires the chunk ladder to sum to at most it.
 */
function zeroUnansweredChunks(ptu: ExtendedBidPtu): ExtendedBidPtu {
  if (!ptu.ptuChunks) {
    return ptu
  }

  return {
    ...ptu,
    ptuChunks: ptu.ptuChunks.map((chunk) =>
      chunk.acceptedVolume == null ? { ...chunk, offeredVolume: { ...chunk.offeredVolume, quantity: 0 } } : chunk,
    ),
  }
}

function newestWhere(bids: ExtendedBid[], predicate: (bid: ExtendedBid) => boolean): ExtendedBid | undefined {
  return bids.filter(predicate).reduce<ExtendedBid | undefined>((newest, bid) => keepNewer(newest, bid), undefined)
}

function newestOf(bids: (ExtendedBid | undefined)[]): ExtendedBid | undefined {
  return bids.reduce<ExtendedBid | undefined>((newest, bid) => keepNewer(newest, bid), undefined)
}

function keepNewer(current: ExtendedBid | undefined, candidate: ExtendedBid | undefined): ExtendedBid | undefined {
  if (!candidate) return current
  if (!current) return candidate

  return isNewer(candidate, current) ? candidate : current
}

/**
 * Portfolio Manager's baseline queries order strictly by `createdAt desc`
 * (`findFirstBy…CreatedAtDesc`), and the goal here is to pick the same row PM would pick - so
 * `createdAt` is the primary key. `id` (a database sequence) only breaks an exact `createdAt` tie,
 * deterministically, which is more than PM itself does: PM resolves that same tie arbitrarily.
 */
function isNewer(candidate: ExtendedBid, current: ExtendedBid): boolean {
  const candidateCreatedAt = candidate.createdAt.valueOf()
  const currentCreatedAt = current.createdAt.valueOf()

  if (candidateCreatedAt !== currentCreatedAt) {
    return candidateCreatedAt > currentCreatedAt
  }

  if (candidate.id !== undefined && current.id !== undefined) {
    return candidate.id > current.id
  }

  return false
}
